import React, { Component } from 'react';
import { connect } from 'react-redux';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { DROPDOWN_CONFIG, DATATABLE_CONFIG, USER_SUBMENU_ID } from '../../../shared/Config';
import { HASACCESS } from '../../../shared/utility';
import Messages from '../../../shared/UI/Messages/Messages';
import * as actions from '../../../store/actions/index';

// Dummy data — replace with API/Redux data
const RAW_MATERIALS = [
    { id: 1, name: 'Tomato', hsnCode: '1234', unit: 'Kg' },
    { id: 2, name: 'Rice',   hsnCode: '5678', unit: 'Kg' },
    { id: 3, name: 'Wheat',  hsnCode: '9101', unit: 'Kg' },
    { id: 4, name: 'Sugar',  hsnCode: '1121', unit: 'Kg' },
];

const CONSUMPTION_DATA = {
    '1_2023-09-01': { consumption: 5,  avgPrice: 20 },
    '1_2023-09-02': { consumption: 8,  avgPrice: 22 },
    '1_2023-09-03': { consumption: 3,  avgPrice: 21 },
    '2_2023-09-01': { consumption: 10, avgPrice: 50 },
    '2_2023-09-02': { consumption: 12, avgPrice: 52 },
    '2_2023-09-03': { consumption: 7,  avgPrice: 51 },
    '3_2023-09-01': { consumption: 20, avgPrice: 30 },
    '3_2023-09-02': { consumption: 0,  avgPrice: 30 },
    '3_2023-09-03': { consumption: 15, avgPrice: 31 },
    '4_2023-09-01': { consumption: 6,  avgPrice: 40 },
    '4_2023-09-02': { consumption: 9,  avgPrice: 42 },
    '4_2023-09-03': { consumption: 4,  avgPrice: 41 },
};

function generateDateRange(from, to) {
    const dates = [];
    const cur = new Date(from);
    const end = new Date(to);
    while (cur <= end) {
        dates.push(new Date(cur));
        cur.setDate(cur.getDate() + 1);
    }
    return dates;
}

function toKey(date) {
    return date.toISOString().split('T')[0];
}

function formatHeaderDate(date) {
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

class ConsumptionReport extends Component {
    state = {
        rawMaterial: '',
        category: null,
        fromDate: new Date('2023-09-01'),
        toDate: new Date('2023-09-03'),
        tableData: [],
        dates: [],
        searched: false,
    };

    componentDidMount() {
        this.getAccess = HASACCESS(USER_SUBMENU_ID);
    }

    buildTableData = (materials, dates) => {
        return materials.map(mat => {
            const row = {
                id: mat.id,
                rawMaterial: mat.name,
                hsnCode: mat.hsnCode,
                unit: mat.unit,
            };
            let totalConsumption = 0;
            let totalCost = 0;
            dates.forEach(date => {
                const key = `${mat.id}_${toKey(date)}`;
                const entry = CONSUMPTION_DATA[key] || { consumption: 0, avgPrice: 0 };
                const cost = entry.consumption * entry.avgPrice;
                const dateStr = toKey(date);
                row[`consumption_${dateStr}`] = entry.consumption;
                row[`avgPrice_${dateStr}`]    = entry.avgPrice;
                row[`cost_${dateStr}`]        = cost;
                totalConsumption += entry.consumption;
                totalCost        += cost;
            });
            row.totalConsumption = totalConsumption;
            row.totalCost        = totalCost;
            return row;
        });
    };

    handleSearch = () => {
        const { fromDate, toDate } = this.state;
        if (!fromDate || !toDate) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'Please select From Date and To Date' });
            return;
        }
        if (fromDate > toDate) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'From Date cannot be after To Date' });
            return;
        }
        const dates     = generateDateRange(fromDate, toDate);
        const tableData = this.buildTableData(RAW_MATERIALS, dates);
        this.setState({ dates, tableData, searched: true });
    };

    handleShowAll = () => {
        const from      = new Date('2023-09-01');
        const to        = new Date('2023-09-03');
        const dates     = generateDateRange(from, to);
        const tableData = this.buildTableData(RAW_MATERIALS, dates);
        this.setState({ fromDate: from, toDate: to, dates, tableData, searched: true });
    };

    handleExportExcel = () => {
        this.growl.show({ severity: 'info', summary: 'Export', detail: 'Export triggered' });
    };

    // Raw material cell — name + HSN code stacked
    rawMaterialBodyTemplate = (rowData) => (
        <div>
            <div style={{ fontWeight: 500 }}>{rowData.rawMaterial}</div>
            {rowData.hsnCode && (
                <div style={{ fontSize: '12px', color: '#888' }}>[ HSN Code: {rowData.hsnCode} ]</div>
            )}
        </div>
    );

    render() {
        if (this.getAccess !== undefined && this.getAccess.viewAccess === false) {
            this.props.history.push('/dashboard');
        }

        const { dates, tableData, fromDate, toDate, searched } = this.state;

        // ── Grouped header ──────────────────────────────────────────────
        // Row 1: "Raw Material" (rowSpan=2) | each date (colSpan=3) | totals (rowSpan=2)
        // Row 2: Consumption | Avg Purchase Price | Consumption Cost  (per date)
        const headerGroup = (
            <ColumnGroup>
                <Row>
                    <Column
                        header="Raw Material"
                        rowSpan={2}
                        style={{ minWidth: '180px', position: 'sticky', left: 0, zIndex: 3, background: '#f4f4f4' }}
                    />
                    {dates.map(date => (
                        <Column
                            key={`dh_${toKey(date)}`}
                            header={formatHeaderDate(date)}
                            colSpan={3}
                            style={{ textAlign: 'center', borderLeft: '1px solid #dee2e6' }}
                        />
                    ))}
                    <Column
                        header="Total Consumption Qty"
                        rowSpan={2}
                        style={{ minWidth: '180px', textAlign: 'center', background: '#fff8e1', borderLeft: '1px solid #dee2e6' }}
                    />
                    <Column
                        header="Total Consumption Cost (₹)"
                        rowSpan={2}
                        style={{ minWidth: '210px', textAlign: 'center', background: '#fff8e1' }}
                    />
                </Row>
                <Row>
                    {dates.flatMap(date => {
                        const d = toKey(date);
                        return [
                            <Column key={`sh_c_${d}`}  header="Consumption"            style={{ minWidth: '130px', textAlign: 'center', borderLeft: '1px solid #dee2e6' }} />,
                            <Column key={`sh_ap_${d}`} header="Avg Purchase Price (₹)" style={{ minWidth: '170px', textAlign: 'center' }} />,
                            <Column key={`sh_cc_${d}`} header="Consumption Cost (₹)"   style={{ minWidth: '170px', textAlign: 'center' }} />,
                        ];
                    })}
                </Row>
            </ColumnGroup>
        );

        return (
            <div className="p-grid padding">
                {this.props.loading  && <div className="page-loader"></div>}
                {this.props.error   !== null && <Messages type="error"   message={this.props.error} />}
                {this.props.success !== null && <Messages type="success" message={this.props.success} />}
                <Toast ref={(el) => this.growl = el} />

                <div className="p-col-12">
                    <div className="card card-w-title" style={{ position: 'relative' }}>
                        <h1>Consumption Report</h1>

                        {/* ── Filter row ───────────────────────────────────── */}
                        <div className="p-grid p-align-end">

                            <div className="p-col-12 p-md-2 p-lg-2 p-d-flex p-flex-column p-gap-2">
                                <label className="label">Raw Material</label>
                                <InputText
                                    placeholder="Raw Material"
                                    className="p-col-12"
                                    name="rawMaterial"
                                    value={this.state.rawMaterial}
                                    onChange={(e) => this.setState({ rawMaterial: e.target.value })}
                                />
                            </div>

                            <div className="p-col-12 p-md-2 p-lg-2 p-d-flex p-flex-column p-gap-2">
                                <label className="label">Category</label>
                                <Dropdown
                                    placeholder="Category"
                                    name="Category"
                                    options={[
                                        { label: 'Select Category', value: null },
                                        { label: 'Category 1', value: 'cat1' },
                                        { label: 'Category 2', value: 'cat2' },
                                    ]}
                                    value={this.state.category || null}
                                    onChange={(e) => this.setState({ category: e.value })}
                                    filter
                                    autoWidth
                                    className="p-col-12"
                                    {...DROPDOWN_CONFIG}
                                />
                            </div>

                            <div className="p-col-12 p-md-2 p-lg-2 p-d-flex p-flex-column p-gap-2">
                                <label className="label">From Date</label>
                                <Calendar
                                    placeholder="From Date"
                                    value={fromDate}
                                    onChange={(e) => this.setState({ fromDate: e.value })}
                                    // dateFormat="d M yy"
                                    // showIcon
                                    // className="p-col-12"
                                />
                            </div>

                            <div className="p-col-12 p-md-2 p-lg-2 p-d-flex p-flex-column p-gap-2">
                                <label className="label">To Date</label>
                                <Calendar
                                    placeholder="To Date"
                                    value={toDate}
                                    onChange={(e) => this.setState({ toDate: e.value })}
                                    // dateFormat="d M yy"
                                    // showIcon
                                    // className="p-col-12"
                                />
                            </div>

                            {/* Search + Show All — inline with filters */}
                            <div className="p-col-12 p-md-4 p-lg-4 p-d-flex p-ai-end p-gap-2">
                                <Button
                                    label="Search"
                                    className="p-button-primary"
                                    onClick={this.handleSearch}
                                />
                                <Button
                                    label="Show All"
                                    className="p-button-secondary p-ml-2"
                                    onClick={this.handleShowAll}
                                />
                            </div>

                            {/* Export — own full-width row, pushed to the right */}
                            <div className="p-col-12 p-d-flex p-jc-end p-mt-1">
                                <Button
                                    label="Export as Excel"
                                    className="p-button-warning"
                                    onClick={this.handleExportExcel}
                                />
                            </div>

                        </div>
                        {/* ── End filter row ───────────────────────────────── */}

                        {/* Note */}
                        {searched && (
                            <div className="p-mt-2 p-mb-2" style={{ fontSize: '13px', color: '#555' }}>
                                <i className="pi pi-info-circle p-mr-1" />
                                Note: The cost of consumption would be available in the excel file when exported.
                            </div>
                        )}

                        {/* ── DataTable ────────────────────────────────────── */}
                        {/* FIX 2: no "frozen" prop — use headerColumnGroup for grouped headers
                            The Raw Material column is the first Column child; PrimeReact will
                            align it under the "Raw Material" header cell automatically.        */}
                        {searched && (
                            <div className="p-col-12" style={{ marginTop: '1em', overflowX: 'auto' }}>
                                <DataTable
                                    value={tableData}
                                    headerColumnGroup={headerGroup}
                                    scrollable
                                    scrollHeight="500px"
                                    scrollDirection="both"
                                    style={{ width: '100%' }}
                                    {...DATATABLE_CONFIG}
                                >
                                    {/* Raw Material column — sticky via CSS, no frozen prop */}
                                    <Column
                                        field="rawMaterial"
                                        body={this.rawMaterialBodyTemplate}
                                        style={{ minWidth: '180px' }}
                                        bodyStyle={{ position: 'sticky', left: 0, zIndex: 1, background: '#fff', borderRight: '1px solid #dee2e6' }}
                                    />

                                    {/* Dynamic date columns (3 per date) */}
                                    {dates.flatMap(date => {
                                        const dateStr = toKey(date);
                                        return [
                                            <Column
                                                key={`consumption_${dateStr}`}
                                                field={`consumption_${dateStr}`}
                                                style={{ minWidth: '130px', textAlign: 'center', borderLeft: '1px solid #dee2e6' }}
                                                body={(row) => `${row[`consumption_${dateStr}`]} / ${row.unit}`}
                                            />,
                                            <Column
                                                key={`avgPrice_${dateStr}`}
                                                field={`avgPrice_${dateStr}`}
                                                style={{ minWidth: '170px', textAlign: 'center' }}
                                            />,
                                            <Column
                                                key={`cost_${dateStr}`}
                                                field={`cost_${dateStr}`}
                                                style={{ minWidth: '170px', textAlign: 'center' }}
                                            />,
                                        ];
                                    })}

                                    {/* Total columns */}
                                    <Column
                                        field="totalConsumption"
                                        style={{ minWidth: '180px', textAlign: 'center', background: '#fff8e1', fontWeight: 600, borderLeft: '1px solid #dee2e6' }}
                                    />
                                    <Column
                                        field="totalCost"
                                        body={(row) => `₹ ${row.totalCost.toLocaleString('en-IN')}`}
                                        style={{ minWidth: '210px', textAlign: 'center', background: '#fff8e1', fontWeight: 600 }}
                                    />
                                </DataTable>
                            </div>
                        )}

                        {/* Empty state */}
                        {!searched && (
                            <div className="p-d-flex p-jc-center p-ai-center" style={{ minHeight: '200px', color: '#aaa', fontSize: '15px' }}>
                                <i className="pi pi-search p-mr-2" />
                                Select a date range and click Search to view the report.
                            </div>
                        )}

                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    loggedUserDetail: state.auth.userObject,
    loading:          state.master.loading,
    error:            state.master.error,
    success:          state.master.success,
    tableLists:       state.master.tableLists,
});

const mapDispatchToProps = dispatch => ({
    dynamicDeleteProcess: (model, data, func) => dispatch(actions.dynamicDeleteProcess(model, data, func)),
    getMasterTableList:   (table)             => dispatch(actions.getMasterTableList(table)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ConsumptionReport);